import Section from "components/layout/section"
import useLayout from "hooks/useLayout"
import { motion, AnimatePresence } from "motion/react"
import Cover from "./Cover"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import React from "react"
import IconButton from "components/buttons/iconButton"
import { APP_CONFIG } from "constants/constants"

interface DialogProps {
    title: string
    dialogId: string
    children: React.ReactNode
    mobileFullScreen?: boolean
    desktopLarge?: boolean
    colorScheme?: "gray" | "red" | "blue" | "green" | "yellow"
    displayCloseButton?: boolean
    /** When set, visibility is controlled by this prop instead of the dialog queue. */
    open?: boolean
    /** Called by the close button and Escape. Omit to keep a controlled dialog open until the caller changes `open`. */
    onClose?: () => void
    className?: string
}

export const DialogContext = React.createContext<any>(null)

const Dialog: React.FC<DialogProps> = ({ title, dialogId, children, mobileFullScreen = false, desktopLarge, colorScheme = "gray", displayCloseButton = true, open, onClose, className }) => {

    const headerClasses = {
        gray: 'bg-gray-100 border-b-gray-200',
        red: 'bg-red-100 border-b-red-200',
        blue: 'bg-blue-100 border-b-blue-200',
        green: 'bg-green-100 border-b-green-200',
        yellow: 'bg-yellow-100 border-b-yellow-200',
    }[colorScheme]

    const headerTextClasses = {
        gray: 'text-gray-800',
        red: 'text-red-800',
        blue: 'text-blue-800',
        green: 'text-green-800',
        yellow: 'text-yellow-800',
    }[colorScheme]

    const {
        dialogQueue,
        closeDialog
    } = useLayout()

    const isControlled = open !== undefined
    const stackIndex = isControlled ? (open ? 0 : -1) : dialogQueue.indexOf(dialogId)
    const isOpen = stackIndex !== -1
    const isTop = isControlled ? isOpen : isOpen && stackIndex === dialogQueue.length - 1
    const depthFromTop = isControlled ? 0 : (isOpen ? dialogQueue.length - 1 - stackIndex : 0)
    const handleClose = isControlled ? onClose : closeDialog
    const titleId = `${dialogId}-title`

    return(
        <>
            <Cover visible={isOpen && (isControlled || stackIndex === 0)} onClose={handleClose} />
            <AnimatePresence>
                {isOpen &&
                    <motion.div
                        role="dialog"
                        aria-modal={isTop}
                        aria-labelledby={titleId}
                        className={`
                            fixed
                            top-0
                            left-0
                            right-0
                            bottom-0
                            flex
                            items-center
                            justify-center
                            ${isTop ? '' : 'pointer-events-none'}
                        `}
                        style={{
                            zIndex: 111 + stackIndex,
                        }}
                        initial={{
                            top: "100vh"
                        }}
                        animate={{
                            top: 0,
                            transition: {
                                duration: APP_CONFIG.SHORT_ENTER_ANIMATION_DURATION
                            }
                        }}
                        exit={{
                            top: "100vh",
                            transition: {
                                duration: APP_CONFIG.SHORT_EXIT_ANIMATION_DURATION
                            }
                        }}
                    >
                        <div
                            className={`
                                relative
                                bg-white
                                rounded-md
                                overflow-x-hidden
                                overflow-y-auto
                                flex
                                flex-col
                                gap-y-4
                                m-2
                                max-h-[calc(100%-1rem)]
                                shadow-xl
                                ${isTop ? 'pointer-events-auto' : 'pointer-events-none'}
                                ${mobileFullScreen ? `h-full w-full lg:h-auto lg:w-auto !m-0 lg:m-2 max-h-full rounded-none lg:rounded-md` : ``}
                                ${desktopLarge ? `lg:!w-[90%] lg:max-w-[72rem]` : ``}
                                ${className ?? ``}
                            `}
                            style={{
                                transform: `translateY(${depthFromTop * -14}px) scale(${1 - depthFromTop * 0.03})`,
                            }}
                        >
                        <div className={`
                            border-b-[1px]
                            min-h-[1lh]
                            shrink-0
                            text-lg
                            sm:text-xl
                            md:text-2xl
                            px-4
                            flex
                            flex-row
                            gap-x-4
                            justify-between
                            items-center
                            h-20
                            ${headerClasses}
                        `}>
                            <h2 id={titleId} className={`${headerTextClasses} min-w-0 whitespace-normal text-[length:inherit] leading-[inherit]`}>{title}</h2>
                            {displayCloseButton && handleClose && <IconButton icon={<CloseIcon />} onClick={() => handleClose()} colorScheme={colorScheme} />}
                        </div>
                            <div className="px-4">
                            <Section design={`spaced`}>
                                {children}
                            </Section>
                            </div>
                        </div>
                    </motion.div>
                }
            </AnimatePresence>
        </>
    )
}

export default Dialog
